import { useEffect, useState } from "react";
import api, { fmtCurrency, fmtAxisCurrency, fmtDate, formatErr } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { useLang } from "../context/LangContext";
import { useAuth } from "../context/AuthContext";
import { isManagerRole } from "../lib/roles";
import {
  Boxes, CircleDollarSign, TrendingDown, Wrench,
  ShieldAlert, CalendarClock, ArrowUpRight, Mail,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";

function MetricCard({ label, value, hint, icon: Icon, testid }) {
  return (
    <div className="bg-white border border-slate-200 p-6" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="label-mono">{label}</div>
        <Icon className="w-4 h-4 text-slate-400" strokeWidth={1.75} />
      </div>
      <div className="font-display text-3xl font-semibold text-slate-900 mt-3">{value}</div>
      {hint && <div className="text-xs text-slate-500 mt-2">{hint}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const { t, lang, fx } = useLang();
  const { user } = useAuth();
  const isAdmin = isManagerRole(user?.role);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).finally(() => setLoading(false));
  }, []);

  const sendReminders = async () => {
    if (!window.confirm(t("due_send_confirm"))) return;
    setSending(true);
    try {
      const { data } = await api.post("/reminders/send-now");
      if (!data.items) toast.info(t("due_send_none"));
      else if (!data.email_configured) toast.error(t("due_send_no_email"));
      else if (!data.emails) toast.error(t("due_send_no_recipients"));
      else toast.success(t("due_sent").replace("{items}", data.items).replace("{emails}", data.emails));
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setSending(false);
    }
  };

  if (loading) return (
    <div className="p-8 text-sm text-slate-500" data-testid="dashboard-loading">{t("loading")}</div>
  );
  if (!stats) return null;

  return (
    <div className="p-6 lg:p-10 max-w-[1600px] mx-auto" data-testid="dashboard-page">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="label-mono mb-2">{t("overview")}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
            {t("asset_dashboard")}
          </h1>
        </div>
        {isAdmin && (
          <Link to="/assets/new" data-testid="dashboard-add-asset"
            className="hidden md:inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm px-4 py-2">
            {t("add_asset")} <ArrowUpRight className="w-4 h-4" strokeWidth={1.75} />
          </Link>
        )}
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200 border border-slate-200 mb-8">
        <MetricCard testid="kpi-total-assets" label={t("total_assets")} value={stats.total_assets} icon={Boxes}
          hint={`${Object.keys(stats.by_category).length} ${t("categories")}`} />
        <MetricCard testid="kpi-book-value" label={t("current_book_value")} value={fmtCurrency(stats.current_book_value)}
          icon={CircleDollarSign} hint={`${t("of_purchased")} ${fmtCurrency(stats.total_purchase_value)} ${t("purchased_label")}`} />
        <MetricCard testid="kpi-depreciated" label={t("acc_depreciation")} value={fmtCurrency(stats.depreciated_value)}
          icon={TrendingDown} hint={t("straight_line")} />
        <MetricCard testid="kpi-open-faults" label={t("open_faults")} value={stats.open_faults}
          icon={Wrench} hint={`${stats.compliance_overdue} ${t("compliance_overdue")}`} />
      </div>

      {lang === "EN" && (
        <p className="text-xs text-slate-500 -mt-5 mb-8" data-testid="fx-note">
          {(fx.source === "fallback" ? t("fx_note_fallback") : t("fx_note"))
            .replace("{rate}", new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(fx.rate))
            .replace("{date}", fx.as_of
              ? new Date(fx.as_of).toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })
              : "-")}
        </p>
      )}

      {/* Chart + Compliance */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        <div className="lg:col-span-2 bg-white border border-slate-200 p-6">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="label-mono mb-1">{t("forecast")}</div>
              <h2 className="font-display text-xl font-medium text-slate-900">{t("book_value_6yr")}</h2>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={stats.depreciation_timeline}>
                <defs>
                  <linearGradient id="dep" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#047857" stopOpacity={0.3} />
                    <stop offset="100%" stopColor="#047857" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="year" stroke="#64748b" fontSize={12} tickLine={false} />
                <YAxis stroke="#64748b" fontSize={12} tickLine={false} width={lang === "ID" ? 78 : 60} tickFormatter={fmtAxisCurrency} />
                <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ background:"white", border:"1px solid #e2e8f0", borderRadius:0, fontSize:12 }} />
                <Area type="monotone" dataKey="value" stroke="#047857" strokeWidth={2} fill="url(#dep)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 p-6">
          <div className="label-mono mb-1">{t("action_required")}</div>
          <h2 className="font-display text-xl font-medium text-slate-900 mb-5">{t("warranty_alerts")}</h2>
          {stats.warranty_alerts.length === 0 ? (
            <div className="text-sm text-slate-500">{t("all_warranties_ok")}</div>
          ) : (
            <div className="space-y-3 max-h-72 overflow-y-auto">
              {stats.warranty_alerts.slice(0, 8).map((w) => (
                <Link to={`/assets/${w.id}`} key={w.id} data-testid={`warranty-alert-${w.id}`}
                  className="block border-l-2 border-amber-400 pl-3 py-1 hover:bg-slate-50">
                  <div className="text-sm font-medium text-slate-900 truncate">{w.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    <span>{w.asset_tag}</span><span>·</span>
                    <span className={w.expired ? "text-rose-600" : "text-amber-700"}>
                      {w.expired ? t("expired") : t("expires")} {fmtDate(w.warranty_end_date)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Inspections & maintenance due */}
      <div className="bg-white border border-slate-200 p-6 mb-6" data-testid="due-items-card">
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <div className="label-mono mb-1">{t("action_required")}</div>
            <h2 className="font-display text-xl font-medium text-slate-900">{t("due_title")}</h2>
          </div>
          {isAdmin && (
            <Button type="button" variant="outline" onClick={sendReminders} disabled={sending}
              className="rounded-none shrink-0" data-testid="send-reminders-button">
              <Mail className="w-3.5 h-3.5 mr-2" strokeWidth={1.75} />
              {sending ? t("due_sending") : t("due_send_now")}
            </Button>
          )}
        </div>
        {(!stats.due_items || stats.due_items.length === 0) ? (
          <div className="text-sm text-slate-500">{t("due_none")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 max-h-72 overflow-y-auto">
            {stats.due_items.slice(0, 12).map((it) => {
              const late = it.days_left < 0;
              const when = late
                ? `${-it.days_left} ${t("due_days_overdue")}`
                : it.days_left === 0
                  ? t("due_today")
                  : `${t("due_in")} ${it.days_left} ${t("due_days")}`;
              return (
                <Link to={`/assets/${it.asset_id}`} key={`${it.asset_id}-${it.kind}`}
                  data-testid={`due-item-${it.asset_id}-${it.kind}`}
                  className={`block border-l-2 ${late ? "border-rose-400" : "border-amber-400"} pl-3 py-1 hover:bg-slate-50`}>
                  <div className="text-sm font-medium text-slate-900 truncate">{it.name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap">
                    <span>{it.asset_tag}</span><span>·</span>
                    <span>{t(`due_${it.kind}`)}</span><span>·</span>
                    <span className={late ? "text-rose-600" : "text-amber-700"}>
                      {fmtDate(it.due_date)} ({when})
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent faults + Categories */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-mono mb-1">{t("recent")}</div>
              <h2 className="font-display text-xl font-medium text-slate-900">{t("fault_reports")}</h2>
            </div>
            <Link to="/faults" className="text-xs text-blue-800 hover:text-blue-900" data-testid="view-all-faults">
              {t("view_all")}
            </Link>
          </div>
          {stats.recent_faults.length === 0 ? (
            <div className="text-sm text-slate-500">{t("no_faults_reported")}</div>
          ) : (
            <div className="space-y-3">
              {stats.recent_faults.map((f) => (
                <div key={f.id} className="flex items-start gap-3 pb-3 border-b border-slate-100 last:border-0">
                  <div className={`w-2 h-2 mt-1.5 rounded-full ${f.severity==="high"?"bg-rose-500":f.severity==="medium"?"bg-amber-500":"bg-slate-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate">{f.title}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                      <span>{f.asset_name}</span><span>·</span>
                      <span className="capitalize">{f.status.replace("_", " ")}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="label-mono mb-1">{t("inventory")}</div>
              <h2 className="font-display text-xl font-medium text-slate-900">{t("by_category")}</h2>
            </div>
            <Link to="/compliance" className="text-xs text-blue-800 hover:text-blue-900" data-testid="view-compliance-link">
              {t("nav_compliance")} →
            </Link>
          </div>
          <div className="space-y-3">
            {Object.entries(stats.by_category).map(([cat, count]) => {
              const pct = (count / stats.total_assets) * 100;
              return (
                <div key={cat}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-slate-700">{cat}</span>
                    <span className="font-mono text-xs text-slate-500">{count}</span>
                  </div>
                  <div className="h-1 bg-slate-100">
                    <div className="h-full bg-blue-800" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          {(stats.compliance_overdue > 0 || stats.compliance_upcoming > 0) && (
            <div className="mt-6 pt-5 border-t border-slate-200 grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <ShieldAlert className="w-4 h-4 text-rose-600" strokeWidth={1.75} />
                <div>
                  <div className="text-xl font-display font-semibold text-slate-900">{stats.compliance_overdue}</div>
                  <div className="text-xs text-slate-500">{t("overdue")}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <CalendarClock className="w-4 h-4 text-amber-600" strokeWidth={1.75} />
                <div>
                  <div className="text-xl font-display font-semibold text-slate-900">{stats.compliance_upcoming}</div>
                  <div className="text-xs text-slate-500">{t("due_30")}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
